/**
 * ReviewKillReasons — why closed deals closed, and how long they took.
 *
 * This lived on Pipeline, where it could only ever mean "all time, every
 * market". Here it inherits the page's date range and MSA filter, which is
 * the only way the question gets interesting: what killed deals THIS quarter
 * against last, or in Broward against Dallas.
 *
 * PASSED and LOST are different businesses and the grid keeps them apart on
 * purpose. Passing is our screen working. Losing is a competitive outcome.
 * Averaged together they hide each other.
 *
 * killMatrix() and cycleTimes() are unchanged from pipelineAnalytics — this
 * component only renders them against a filtered set of deals.
 */

import { useMemo } from "react";
import * as B from "../common/brand";
import { HeatGrid, Explain } from "../common/Charts";
import { killMatrix, cycleTimes } from "../../services/pipelineAnalytics";

const sf0 = (v) => (Number.isFinite(v) && v ? Math.round(v).toLocaleString() : "—");

export default function ReviewKillReasons({ deals = [], dateLabel = "" }) {
  const kills = useMemo(() => killMatrix(deals), [deals]);
  const cycle = useMemo(() => cycleTimes(deals), [deals]);

  const totalReasoned = kills.empty ? 0 : kills.ranked.reduce((s, r) => s + r.n, 0);

  return (
    <div className="bg-[rgba(44,44,44,0.6)] border border-[rgba(58,58,58,0.5)] rounded-xl">
      <header className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
        <div className="min-w-0">
          <h3 className={B.TITLE}>Why deals die</h3>
          <p className={`${B.SUBTITLE} mt-0.5`}>
            {kills.empty
              ? "no reasons recorded in this window"
              : `${kills.ranked.length} reason${kills.ranked.length === 1 ? "" : "s"} across ${totalReasoned} closed deal${totalReasoned === 1 ? "" : "s"}`}
            {dateLabel ? ` · ${dateLabel}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="hidden sm:flex items-center gap-4">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#8a8a8a]">Median in UW</div>
              <div className="text-[14px] font-light tabular-nums text-[#f4f4f4]">
                {cycle.uwDays == null ? "—" : `${Math.round(cycle.uwDays)}d`}
                <span className="ml-1 text-[10.5px] text-[#8a8a8a]">
                  {cycle.uwN ? `· ${cycle.uwN}` : ""}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#8a8a8a]">Median to LOI</div>
              <div className="text-[14px] font-light tabular-nums text-[#f4f4f4]">
                {cycle.loiDays == null ? "—" : `${Math.round(cycle.loiDays)}d`}
                <span className="ml-1 text-[10.5px] text-[#8a8a8a]">
                  {cycle.loiN ? `· ${cycle.loiN}` : ""}
                </span>
              </div>
            </div>
            <Explain label="?">{cycle.how}</Explain>
          </div>
          <Explain>{kills.how}</Explain>
        </div>
      </header>

      {kills.empty ? (
        <div className="px-5 pb-6 text-[12.5px] text-[#8a8a8a] leading-relaxed">
          No closed deal in this window carries a recorded reason, so there is
          nothing to count. The reason is captured when a deal is moved to
          Passed, Lost or Dead — it is the one field that turns a dead pipeline
          into an argument about what to do differently.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 px-5 pb-4">
          <div>
            <div className={`${B.LABEL} mb-2`}>Reason against how it ended</div>
            <HeatGrid rows={kills.rows} cols={kills.cols} cells={kills.cells}
              fmt={(v) => String(Math.round(v))} rowLabelWidth={190} empty="Nothing to shade." />
          </div>
          <div>
            <div className={`${B.LABEL} mb-2`}>Ranked by how often, with the area behind it</div>
            <div className="space-y-1.5 max-h-[15rem] overflow-y-auto pr-1">
              {kills.ranked.map((r) => {
                const max = kills.ranked[0].n || 1;
                return (
                  <div key={r.reason} className="flex items-center gap-3">
                    <span className="w-52 shrink-0 text-[11.5px] text-[#b4b4b4] truncate" title={r.reason}>{r.reason}</span>
                    <div className="flex-1 h-2 rounded-full bg-[#2c2c2c] overflow-hidden">
                      <div className="h-full rounded-full bg-[rgba(233,53,41,0.5)]" style={{ width: `${(r.n / max) * 100}%` }} />
                    </div>
                    <span className="w-7 text-right text-[11.5px] tabular-nums text-[#f4f4f4]">{r.n}</span>
                    <span className="w-20 text-right text-[10.5px] tabular-nums text-[#8a8a8a]">{sf0(r.sf)} SF</span>
                  </div>
                );
              })}
            </div>
            {kills.missing > 0 && (
              <p className="mt-3 text-[10.5px] text-[#7a7a7a]">
                {kills.missing} closed deal{kills.missing === 1 ? "" : "s"} carry no reason and are counted nowhere above.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
